import { ProjectionProviderType, ProviderNetworkType } from "@app/projects/enumerators";
import { ProjectionPanelMode, ProjectionProviderDetails } from "@app/projects/projection-provider";
import { ImpactProviderMapResults, NetworkProviderMapResults } from "@app/projects/results/common/mapping";
import { ProjectionProvider } from "@app/projects/results/projection-provider";
import { ServicingProvider } from "@app/projects/results/servicing-provider";
import { SimulateProviderTargeting } from "@app/projects/servicing-provider/SimulateProviderTargeting";
import ProviderBadge from "@app/shared/ProviderBadge";
import { RoleRestricted, Roles, UserContext } from "@app/user";
import { JSX, useContext, useReducer, useRef, useState } from "react";
import { Popup } from "react-map-gl/mapbox";
import { FilterContext } from "./contexts/FilterContext";
import { FeatureFlagContext, Features } from "@app/feature-flags";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { ServicingProviderProfile } from "@app/projects/servicing-provider";
import projectionProviderDetailsReducer, { projectionProviderDetailsInitialState } from "@app/projects/projection-provider/projectionProviderDetailsReducer";
import { FlyoutLink } from "@app/shared/Flyouts";
import { Modal } from "@app/shared/Modals";
import DeleteProjectionProvider, { WrappedDeleteProjectionProvider } from "@app/projects/projection-provider/DeleteProjectionProvider";
import { QESButton } from "@app/shared/qes-standards";

type NetworkCoverageMapProviderPopupProps = {
  provider: NetworkProviderMapResults | ImpactProviderMapResults
  onClose: () => void;
  processProviderChanges: (reprocessFailure: boolean) => Promise<void>;
  setProjectHasUnprocessedProjectionProviders: (has: boolean) => void;
};

const NetworkCoverageMapProviderPopup: React.FC<NetworkCoverageMapProviderPopupProps> = ({ provider, onClose, processProviderChanges, setProjectHasUnprocessedProjectionProviders }) => {

  const filterContext = useContext(FilterContext);
  const featureFlagContext = useContext(FeatureFlagContext);
  const projectContext = useContext(CountyProjectContext);
  const userContext = useContext(UserContext);
  const [projectionProvidersState, projectionProvidersReducer] = useReducer(projectionProviderDetailsReducer, projectionProviderDetailsInitialState);

  const [isDeleteProjectionProviderModalOpen, setIsDeleteProjectionProviderModalOpen] = useState<boolean>(false);

  const deleteProjectionProviderRef = useRef<WrappedDeleteProjectionProvider>(null);

  const isNetworkProvider = !!(provider as ImpactProviderMapResults).providerNetworkType ? false : true;
  const isTargetProvider = !!(provider as ImpactProviderMapResults).projectionType && (provider as ImpactProviderMapResults).projectionType == ProjectionProviderType.Target;
  const isTargetRestricted = projectContext.project.isReadOnly || projectContext.project.isEditRestricted;

  const buildServicingProviderFromProviderMapResults = (provider: NetworkProviderMapResults | ImpactProviderMapResults): ServicingProvider => {
      return {
        address: provider.address,
        address2: provider.address2,
        city: provider.city,
        contractType: null,
        countyFIPS: null,
        countySSA: null,
        credentials: provider.credentials,
        deactivatedNPI: false,
        displayName: provider.displayName,
        employmentStatus: null,
        entityType: provider.entityType,
        externLocationId: null,
        externProviderId: null,
        externRowId: null,
        externSpecialtyCode: null,
        externZip: null,
        firstName: provider.firstName,
        gender: null,
        geoInfoDisplay: null,
        invalidNPI: false,
        lastName: provider.lastName,
        latitude: provider.latitude,
        longitude: provider.longitude,
        medGroupAffiliation: null,
        medicareCCN: null,
        medicareCertifiedBeds: null,
        medicareOptOut: false,
        middleName: provider.middleName,
        missingNPI: false,
        name: provider.displayName,
        npi: provider.npi,
        oigExcluded: false,
        oigWaiver: false,
        organizationNPI: provider.organizationNPI,
        organizationName: provider.organizationName,
        planningRegion: null,
        precluded: false,
        prefix: provider.prefix,
        processed: false,
        providerId: provider.providerId,
        servesPercent: provider.servesPercent,
        specialty: null,
        specialtyCode: provider.specialtyCode,
        state: provider.state,
        suffix: provider.suffix,
        taxonomyCode: null,
        tin: null,
        underContract: null,
        zip: provider.zip
      };
    };
  
    const getNetworkTypeFromProvider = (provider: NetworkProviderMapResults | ImpactProviderMapResults): ProviderNetworkType => {
      if (!!(provider as ImpactProviderMapResults).providerNetworkType) {
        return (provider as ImpactProviderMapResults).providerNetworkType;
      }
      else {
        return ProviderNetworkType.Network;
      }
    };
  
    const getPriorProjectionProvider = (projectionType: ProjectionProviderType) => {
   
      if (projectionType === ProjectionProviderType.Unknown) {
        return null;
      } else {
        return projectionType;
      }
    };

  const getProviderFlyout = (): React.ReactNode => {
    if (!!provider) {
      return (
        <ServicingProviderProfile
          isProjected={
                    !!(provider as ImpactProviderMapResults) &&
                    (provider as ImpactProviderMapResults).projectionType !== ProjectionProviderType.Unknown }
          isTerminated={false}
          nextClosestProvider={false}
          project={projectContext.project}
          provider={buildServicingProviderFromProviderMapResults(provider)}
          regionSetId={parseInt(filterContext.selectedServiceArea)}
          runConfiguration={projectContext.runConfiguration}
          providerNetworkType={getNetworkTypeFromProvider(provider)}
          specialtyGroupCode={filterContext.selectedSpecialty}
          isSSAToFIPSFeatureEnabled={featureFlagContext.enabledFeatureFlags.has(Features.SSA_TO_FIPS)}
        />
      );
    }
  };

  const getPreviewTargetingContent = (): React.ReactNode => {
    const showAvgDist = projectContext.runConfiguration.showDistance && projectContext.project.showAverageDistance;
    const showAvgTime = projectContext.runConfiguration.showTime && projectContext.project.showAverageTime;
    const showWithTimeAndDistanceColumns = projectContext.runConfiguration.showWithTimeAndDistanceColumns;
    const showNetNew = userContext.roles.includes(Roles.VOLUME_TARGET);
    const selectedProvider = provider as ImpactProviderMapResults;

    return (
      <SimulateProviderTargeting
        processingContent={(() => getCreateProjectionProvider(ProjectionProviderType.Target, getPriorProjectionProvider(selectedProvider.projectionType)))()}
        projectionProviderType={ProjectionProviderType.Target}
        providerNetworkType={getNetworkTypeFromProvider(selectedProvider)}
        regionSetId={parseInt(filterContext.selectedServiceArea)}
        selectedProviders={[buildServicingProviderFromProviderMapResults(selectedProvider)]} //In mapping v2, so far we support targetting individual providers only
        specialtyGroupCode={filterContext.selectedSpecialty}
        visibility={{ showAvgDist, showAvgTime, showNetNew, showWithTimeAndDistanceColumns, }}
        user={userContext}
        formState={projectionProvidersState}
        formDispatch={projectionProvidersReducer}
        isZipProject={false}
      />
    );
  };

  const getCreateProjectionProvider = (projectionProviderType: ProjectionProviderType, priorProjectionProviderType: ProjectionProviderType) => {
    let priorProjectionProviderProjectIds: number[];
    const selectedProvider = provider as ImpactProviderMapResults;
    const selectedProviders: ServicingProvider[] = [buildServicingProviderFromProviderMapResults(selectedProvider)];

    if (priorProjectionProviderType) {
      priorProjectionProviderProjectIds =
        selectedProviders.map((r) => {
          const projectionProvider = r as ProjectionProvider;
          return projectionProvider.projectionProviderProjectId;
        });
    }

    return (
      <ProjectionProviderDetails
        formState={projectionProvidersState}
        formDispatch={projectionProvidersReducer}
        hasProvidersWithoutNPI={selectedProviders.some((provider) => provider.npi === undefined)}
        networkAssessmentAnalysisType={projectContext.runConfiguration.networkAssessmentAnalysisType}
        priorProjectionProviderProjectIds={priorProjectionProviderProjectIds}
        priorProjectionProviderType={priorProjectionProviderType}
        projectId={projectContext.project.projectId}
        projectionPanelMode={ProjectionPanelMode.Create}
        projectionProviderType={projectionProviderType}
        selectedProviders={selectedProviders}
        providerNetworkType={getNetworkTypeFromProvider(selectedProvider)}
        onProjectionProvidersCreated={handleProjectionProviderCreatedOrUpdated}
        specialtyGroupCode={filterContext.selectedSpecialty}
        isZipProject={false}
      />
    );
  }

  const getDeleteProjectionProvider = () => {
    const selectedProvider = provider as ImpactProviderMapResults;

    return (
      <DeleteProjectionProvider
        netNewServicingProviderProjectIds={[]}
        projectId={projectContext.project.projectId}
        projectionProviderProjectIds={[selectedProvider.projectionProviderProjectId]}
        projectionProviderType={selectedProvider.projectionType}
        providerNetworkType={getNetworkTypeFromProvider(selectedProvider)}
        onProjectionProvidersDeleted={handleProjectionProviderDeleted}
        wrapperComponentType="modal"
        ref={deleteProjectionProviderRef}
      />
    );
  }

  const handleProjectionProviderCreatedOrUpdated = async (runProjection: boolean, hasUnprocessedProviders = true) => {
    if (runProjection) {
      processProviderChanges(false);
    } else {
      setProjectHasUnprocessedProjectionProviders(hasUnprocessedProviders);
    }
  }

  const handleProjectionProviderDeleted = async (runProjection: boolean) => {
    if (runProjection) {
      processProviderChanges(false);
    } else {
      setProjectHasUnprocessedProjectionProviders(true);
    }
    setIsDeleteProjectionProviderModalOpen(false);
  }

  const handleConfirmDeleteProjectionProvider = async () => {
    //Execute the handleSubmit method in the DeleteProjectionProvider component
    await deleteProjectionProviderRef.current?.handleSubmit();
  };

  return (

    <Popup
      anchor="bottom"
      closeButton={true}
      closeOnClick={false}
      latitude={provider.latitude}
      longitude={provider.longitude}
      onClose={onClose}
      offset={[0, -20]}
    >
      <div className="network-coverage-map__provider-popup">

        <div className="provider-popup__title">{provider.displayName}</div>

        <div className="provider-popup__separator" />

        <div className="provider-popup__badge">
          <ProviderBadge providerNetworkType={isNetworkProvider ? ProviderNetworkType.Network :
            (provider as ImpactProviderMapResults).providerNetworkType == ProviderNetworkType.Prospect ? ProviderNetworkType.Prospect :
              ProviderNetworkType.Market
          } />
          {isTargetProvider &&
            <ProviderBadge providerNetworkType={ProjectionProviderType.Target} />
          }
        </div>

        <div className="provider-popup__row">
          <strong>NPI:</strong> {provider.npi}
        </div>

        <div className="provider-popup__row">
          <strong>Address:</strong> {provider.address}, {provider.city}, {provider.state} {provider.zip}
        </div>

        <div className="provider-popup__row">
          {isNetworkProvider ?
            <>
              <strong>Serves:</strong> {provider.servesPercent}%
            </>
            :
            <>
              <strong>Gains:</strong> {(provider as ImpactProviderMapResults).gainsPercent}% <strong className="impact-label">Impact:</strong> {(provider as ImpactProviderMapResults).impactsPercent}%
            </>
          }
        </div>

        {/* Provider Details flyout */}
        <div className="provider-popup__row">
          <FlyoutLink
            classNames="provider-details-button" 
            content={{
              body: getProviderFlyout() as JSX.Element,
              title: 'Provider Profile',
            }}
            size="40"
            testId='network-coverage-map__provider-popup-view-provider-details-button-flyout'>
                View Provider Details
          </FlyoutLink>
        </div>

        {!isNetworkProvider && !isTargetProvider && (
          <RoleRestricted allowedRoles={[Roles.PROVIDER_TARGET_USER]}> 
            <div className="provider-popup__row">
              {/* Target provider flyout without preview */}
              <RoleRestricted deniedRoles={[Roles.SIMULATED_PROJECTION]}>
                <FlyoutLink
                  classNames="qes-button-primary" 
                  content={{
                    body: getCreateProjectionProvider(ProjectionProviderType.Target, getPriorProjectionProvider((provider as ImpactProviderMapResults).projectionType)),
                    title: `Target Provider`
                  }}
                  disabled={isTargetRestricted}
                  testId='network-coverage-map__provider-popup-target-provider-button-flyout-without-preview'>
                  Target Provider
                </FlyoutLink>
              </RoleRestricted>

              {/* Target provider flyout with preview */}
              <RoleRestricted allowedRoles={[Roles.SIMULATED_PROJECTION]}>
                <FlyoutLink
                  classNames="qes-button-primary" 
                  content={{
                    body: getPreviewTargetingContent() as JSX.Element,
                    title: `Target Provider`
                  }}
                  disabled={isTargetRestricted}
                  testId='network-coverage-map__provider-popup-target-provider-button-flyout-with-preview'>
                  Target Provider
                </FlyoutLink>
              </RoleRestricted>
            </div> 
          </RoleRestricted>
        )}

        {isTargetProvider && (
          <RoleRestricted allowedRoles={[Roles.PROVIDER_TARGET_USER]}>
            <div className="provider-popup__row">
              <QESButton
                qesType="primary"
                disabled={isTargetRestricted}
                onClick={() => setIsDeleteProjectionProviderModalOpen(true)}
                qesTestId="network-coverage-map__provider-popup-remove-provider-from-targeting-modal">
                Remove Target
              </QESButton>
              <Modal
                closeModal={() => setIsDeleteProjectionProviderModalOpen(false)}
                isVisible={isDeleteProjectionProviderModalOpen}
                content={{
                  body: getDeleteProjectionProvider(),
                  cancelButtonText: "Cancel",
                  confirmButtonAction: handleConfirmDeleteProjectionProvider,
                  confirmButtonText: "Submit",
                  confirmButtonType: "primary",
                  title: "Delete Targeted Provider",
                }}
                
              />
            </div>
          </RoleRestricted>
        )}

      </div>

    </Popup>

  );
};

export default NetworkCoverageMapProviderPopup;